/**
 * Romanian CNP (personal numeric code) helpers.
 * The first digit encodes the century, digits 2–3 the birth year within it.
 */

export function isValidCnpFormat(cnp: string): boolean {
  return /^\d{13}$/.test(cnp);
}

/** Multipliers for the check-digit algorithm (mirrors the mobile `CnpValidator`). */
const CNP_CHECK_MULTIPLIERS = [2, 7, 9, 1, 4, 6, 3, 5, 8, 2, 7, 9];

/**
 * Full CNP validation: 13 digits + check digit (cifra de control). Does NOT
 * validate sex/century, birth date, or county code — those are intentionally
 * skipped because real-world CNPs occasionally fail one of them (foreign
 * residents, historical county codes, etc.), but the checksum is mandated by
 * the spec. Ported 1:1 from the mobile app's `CnpValidator.isValid`.
 */
export function isValidCnp(cnp: string): boolean {
  if (!isValidCnpFormat(cnp)) return false;
  const sum = CNP_CHECK_MULTIPLIERS.reduce((acc, mult, i) => acc + mult * Number(cnp.charAt(i)), 0);
  const remainder = sum % 11;
  const expected = remainder === 10 ? 1 : remainder;
  return Number(cnp.charAt(12)) === expected;
}

/**
 * Derives the 4-digit birth year from a CNP, or null if the format is invalid.
 *   1,2 → 1900s · 3,4 → 1800s · 5,6,7,8 → 2000s
 */
export function birthYearFromCnp(cnp: string): number | null {
  if (!isValidCnpFormat(cnp)) return null;
  const prefix = Number(cnp[0]);
  const yy = Number(cnp.slice(1, 3));
  switch (prefix) {
    case 1:
    case 2:
      return 1900 + yy;
    case 3:
    case 4:
      return 1800 + yy;
    case 5:
    case 6:
    case 7:
    case 8:
      return 2000 + yy;
    default:
      return null;
  }
}
